<template>
    <div class="table-responsive">
        <table
            class="table table-light b-table"
            :class="{'table-striped': !hasChildColumns}"
            :style="tableStyle">
            <caption
                class="caption-top visually-hidden"
                v-if="caption != ''">
                {{
                    caption
                }}
            </caption>

            <thead v-if="showHeader">
                
                <tr>
                    <th
                        v-for="column in columns"
                        :key="column.key"
                        :id="`${tableId}-col-${column.key}`"
                        :style="column.columnStyle || {}"
                        :class="column.columnClass || []"
                        :colspan="hasChildren(column) ? column.children!.length : 1"
                        :rowspan="hasChildren(column) ? 1 : hasChildColumns ? 2 : 1"
                        :aria-sort="ariaSort(column)">
                        <slot
                            :name="`header.${column.key}`"
                            v-bind:column="column">
                            <div class="me-2">
                                <template v-if="column.sortable">
                                    <button type="button" @click="onColumnClick(column)" class="btn btn-link link-dark p-0 border-0 align-baseline fw-bold text-nowrap">
                                        {{ column.text }}
                                    </button>
                                    <i aria-hidden="true" class="bi bi-arrow-up position-absolute" :class="isAscending(column) ? '' :'invisible'" v-tooltip="'Sorted asc'"></i>
                                    <i aria-hidden="true" class="bi bi-arrow-down position-absolute" :class="isDescending(column) ? '' :'invisible'" v-tooltip="'Sorted desc'"></i>
                                </template>                         
                                <span v-else>
                                    {{ column.text }}
                                </span>                            
                            </div>
                        </slot>
                        <slot
                            :name="`header.bottom.${column.key}`"
                            v-bind:column="column"></slot>
                    </th>
                </tr>

                
                <tr v-if="hasChildColumns">
                    <template
                        v-for="column in columns"
                        :key="column.key">
                        <template v-if="hasChildren(column)">
                            <th
                                v-for="child in column.children"
                                :key="child.key"
                                :id="`${tableId}-col-${child.key}`"
                                :style="child.columnStyle || {}"
                                :class="child.columnClass || []"
                                :aria-sort="ariaSort(child)">
                                <slot
                                    :name="`header.${child.key}`"
                                    v-bind:column="child">
                                    <div class="me-2">
                                        <a
                                            v-if="child.sortable"
                                            @click.prevent="onColumnClick(child)"
                                            href=""
                                            class="link-dark">
                                            {{ child.text }}
                                        </a>
                                        <span v-else>{{ child.text }}</span>
                                        <i
                                            aria-hidden="true"
                                            class="bi bi-arrow-up position-absolute"
                                            :class="isAscending(child) ? '' : 'invisible'"
                                            v-tooltip="'Sorted asc'"></i>
                                        <i
                                            aria-hidden="true"
                                            class="bi bi-arrow-down position-absolute"
                                            :class="isDescending(child) ? '' : 'invisible'"
                                            v-tooltip="'Sorted desc'"></i>
                                    </div>
                                </slot>
                            </th>
                        </template>
                    </template>
                </tr>
            </thead>

            <tbody v-if="items != null && items.length > 0">
                <template
                    v-for="(item, itemIndex) in items"
                    :key="item.id">

                    <template v-if="!hasChildColumns">
                        <tr>
                            <td
                                v-for="column in columns"
                                :key="column.key"
                                :headers="`${tableId}-col-${column.key}`"
                                :style="column.style ? column.style(item) : {}"
                                :class="column.class ? column.class(item) : []"
                                class="align-middle">
                                <slot
                                    :name="`cell.${column.key}`"
                                    v-bind:column="column"
                                    v-bind:item="item"
                                    >{{ column.value ? column.value(item) : item }}</slot
                                >
                            </td>
                        </tr>
                    </template>

                    
                    <template v-else>
                        
                        <template
                            v-for="(subRow, subIdx) in getSubRows(item)"
                            :key="subIdx">
                            <tr :class="{'b-table-group-striped': itemIndex % 2 === 0}">
                                <template
                                    v-for="column in columns"
                                    :key="column.key">
                                    
                                    <th
                                        v-if="!hasChildren(column) && subIdx === 0"
                                        scope="row"
                                        :id="`${tableId}-row-${itemIndex}`"
                                        :headers="`${tableId}-col-${column.key}`"
                                        :rowspan="getSubRows(item).length || 1"
                                        :style="column.style ? column.style(item) : {}"
                                        :class="[column.class ? column.class(item) : [], 'b-table-identity-header']"
                                        class="align-middle">
                                        <slot
                                            :name="`cell.${column.key}`"
                                            v-bind:column="column"
                                            v-bind:item="item"
                                            >{{ column.value ? column.value(item) : item }}</slot
                                        >
                                    </th>


                                    <template v-if="hasChildren(column)">
                                        <td
                                            v-for="child in column.children"
                                            :key="child.key"
                                            :headers="`${tableId}-col-${column.key} ${tableId}-col-${child.key} ${tableId}-row-${itemIndex}`"
                                            :style="child.style ? child.style(subRow) : {}"
                                            :class="child.class ? child.class(subRow) : []"
                                            class="align-middle">
                                            <slot
                                                :name="`cell.${child.key}`"
                                                v-bind:column="child"
                                                v-bind:item="item"
                                                v-bind:subRow="subRow"
                                                >{{
                                                    child.subRowValue
                                                        ? child.subRowValue(item, subRow)
                                                        : child.value
                                                        ? child.value(subRow)
                                                        : subRow
                                                }}</slot
                                            >
                                        </td>
                                    </template>
                                </template>
                            </tr>
                        </template>

                        
                        <tr
                            v-if="getSubRows(item).length === 0"
                            :class="{'b-table-group-striped': itemIndex % 2 === 0}">
                            <template
                                v-for="column in columns"
                                :key="column.key">
                                <td
                                    v-if="!hasChildren(column)"
                                    :headers="`${tableId}-col-${column.key}`"
                                    :style="column.style ? column.style(item) : {}"
                                    :class="column.class ? column.class(item) : []"
                                    class="align-middle">
                                    <slot
                                        :name="`cell.${column.key}`"
                                        v-bind:column="column"
                                        v-bind:item="item"
                                        >{{ column.value ? column.value(item) : item }}</slot
                                    >
                                </td>
                                <template v-else>
                                    <td
                                        v-for="child in column.children"
                                        :key="child.key"
                                        :headers="`${tableId}-col-${column.key} ${tableId}-col-${child.key}`"
                                        class="align-middle text-muted">
                                    </td>
                                </template>
                            </template>
                        </tr>
                    </template>
                </template>
            </tbody>

            <tbody v-else>
                <tr>
                    <td
                        :colspan="totalColumnCount"
                        role="status"
                        aria-live="polite"
                        aria-atomic="true">
                        No data to display at this time
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script setup lang="ts">
import {ref, computed, useId} from "vue";
import {BTableColumn, BTableOptions} from "./BTable.types";

const tableId = useId();

const props = withDefaults(
    defineProps<{
        items: any;
        columns: BTableColumn[];
        showHeader?: boolean;
        value: BTableOptions;
        caption: string;
        tableStyle?: string;
    }>(),
    {
        items: () => [],
        columns: () => [] as BTableColumn[],
        showHeader: true,
        value: () =>
            ({
                totalItemCount: 0,
                itemsPerPage: 1,
                page: 1,
                sortBy: [],
            } as BTableOptions),
        caption: "",
        tableStyle: "",
    }
);

const tableOptions = ref(props.value as BTableOptions);
const emit = defineEmits(["optionChange"]);

function hasChildren(col: BTableColumn): boolean {
    return !!(col.children && col.children.length > 0);
}

const hasChildColumns = computed(() => props.columns.some(hasChildren));

const totalColumnCount = computed(() =>
    props.columns.reduce((sum, col) => sum + (hasChildren(col) ? col.children!.length : 1), 0)
);

function getSubRows(item: any): any[] {
    const parent = props.columns.find(hasChildren);
    if (!parent) return [];
    if (parent.subRows) return parent.subRows(item) ?? [];
    const v = parent.value ? parent.value(item) : null;
    return Array.isArray(v) ? v : [];
}

function isAscending(column: BTableColumn): boolean {
    const s = tableOptions.value.sortBy[0];
    return s && s.key == column.key && !s.isDescending;
}

function isDescending(column: BTableColumn): boolean {
    const s = tableOptions.value.sortBy[0];
    return s && s.key == column.key && s.isDescending;
}

function ariaSort(column: BTableColumn): "ascending" | "descending" | "none" | undefined {
    if (!column.sortable) return undefined;
    if (isAscending(column)) return "ascending";
    if (isDescending(column)) return "descending";
    return "none";
}

function onColumnClick(column: BTableColumn) {
    const s = tableOptions.value.sortBy[0];

    if (s && s.key == column.key) {
        if (s.isDescending) {
            tableOptions.value.sortBy = [];
        } else {
            tableOptions.value.sortBy[0].isDescending = true;
        }
    } else {
        tableOptions.value.sortBy = [
            {
                key: column.key,
                isDescending: false,
            },
        ];
    }
    tableOptions.value.page = 1;

    emit("optionChange", tableOptions.value);
}
</script>

<style lang="scss">
.b-table {
    thead,
    tbody,
    tfoot,
    tr,
    td,
    th {
        min-width: max-content;
    }

    th {
        vertical-align: text-top;
    }

    tbody td a,
    tbody th a {
        text-decoration: underline;
    }

    // The row-spanning identity cell is now a <th> for a11y (WCAG H43);
    // this restores its visual appearance to match the sibling <td> cells.
    th.b-table-identity-header {
        font-weight: normal;
        text-align: left;
    }

    
    tr.b-table-group-striped > * {
        --b2020-table-accent-bg: var(--b2020-table-striped-bg);
        --bs-table-color-type: var(--bs-table-striped-color);
        --bs-table-bg-type: var(--bs-table-striped-bg);
        color: var(--b2020-table-striped-color);
    }
}
</style>
